import type {
  TransactionEvent,
  TxType,
  TxStatus,
  Product,
  CorridorStat,
} from "./types";
import type { MerchantStat } from "../overlays/TopMerchants";
import { resolveCorridor, seededHubCity } from "./geocode";
import { resolveMerchantHQ, merchantLabel } from "./merchants";
import { CITIES } from "./corridors";

export type TodayTotals = {
  total: { count: number; volumeUsd: number };
  byProduct: Record<Product, { count: number; volumeUsd: number }>;
  /** Midnight (EAT) as unix ms — useful for rendering an "as of" line. */
  sinceMs: number;
};

/**
 * Day boundary timezone for "TODAY" totals.
 *
 * GMT/UTC so the display matches Eversend's group-wide reporting and
 * the Metabase dashboards (which also use UTC days).
 */
const DAY_OFFSET_MS = 0;

/** Unix ms of today's 00:00 in the reporting timezone. */
function midnightMs(nowMs = Date.now()): number {
  const localNow = new Date(nowMs + DAY_OFFSET_MS);
  return (
    Date.UTC(
      localNow.getUTCFullYear(),
      localNow.getUTCMonth(),
      localNow.getUTCDate(),
    ) - DAY_OFFSET_MS
  );
}

export function emptyProductTotals(): TodayTotals["byProduct"] {
  return {
    send_money: { count: 0, volumeUsd: 0 },
    add_money: { count: 0, volumeUsd: 0 },
    exchange_money: { count: 0, volumeUsd: 0 },
    card: { count: 0, volumeUsd: 0 },
    card_creation: { count: 0, volumeUsd: 0 },
    other: { count: 0, volumeUsd: 0 },
  };
}

export function emptyTodayTotals(): TodayTotals {
  return {
    total: { count: 0, volumeUsd: 0 },
    byProduct: emptyProductTotals(),
    sinceMs: midnightMs(),
  };
}

/**
 * Top corridors from the replay feed — computed from real transaction
 * timestamps, not session-accumulated history. Filters to send_money + card
 * (the products that actually move money across borders).
 *
 * Windows back from the newest event so the panel populates immediately
 * on page load, regardless of how much the viewer has watched.
 */
function computeTopCorridors(
  events: RawEvent[],
  limit: number,
): CorridorStat[] {
  if (!events.length) return [];
  const cutoff = midnightMs(); // today since midnight GMT
  const byKey = new Map<string, CorridorStat>();
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.ts < cutoff) break;
    const product = resolveProduct(e.product);
    if (product !== "send_money") continue;

    // Normalize ISO-3 → ISO-2 for display parity with the send data.
    const src = (e.srcCountry ?? e.srcCcy ?? "").toUpperCase();
    const dst = (e.dstCountry ?? e.dstCcy ?? "").toUpperCase();
    if (!src || !dst) continue;
    const key = `${src}->${dst}`;
    const existing = byKey.get(key) ?? {
      key,
      countryA: src,
      countryB: dst,
      volumeUsd60m: 0,
      count60m: 0,
    };
    existing.volumeUsd60m += e.usd;
    existing.count60m += 1;
    byKey.set(key, existing);
  }
  return [...byKey.values()]
    .sort((a, b) => b.volumeUsd60m - a.volumeUsd60m)
    .slice(0, limit);
}

/**
 * Baseline today's totals — events from the replay file that occurred today
 * (GMT) but BEFORE the tail playback window begins. These events will never
 * reach the onEvent emission (they're pre-tail), so we account for them once
 * here. Events inside the tail window are counted via live emission instead,
 * so the displayed totals tick up as arcs / pulses actually appear.
 */
/**
 * Top merchants from card spend in the rolling window.
 */
function computeTopMerchants(
  events: RawEvent[],
  limit: number,
): MerchantStat[] {
  if (!events.length) return [];
  const cutoff = midnightMs(); // since midnight GMT, matches corridors
  const byName = new Map<string, MerchantStat>();
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.ts < cutoff) break;
    if (e.product !== "card" || !e.merchant) continue;
    const clean = merchantLabel(e.merchant) ?? e.merchant;
    const existing = byName.get(clean) ?? { name: clean, count: 0, volumeUsd: 0 };
    existing.count += 1;
    existing.volumeUsd += e.usd;
    byName.set(clean, existing);
  }
  return [...byName.values()]
    .sort((a, b) => b.volumeUsd - a.volumeUsd)
    .slice(0, limit);
}

/**
 * Compute per-city activity (0..1) from the last 60 minutes of real event
 * timestamps. Both origin and destination cities are credited per event.
 * Activity is log-normalized so the busiest city = 1.0 and quiet cities
 * are visible but clearly dimmer.
 */
export function computeCityActivity(events: RawEvent[]): Map<number, number> {
  if (!events.length) return new Map();
  const cutoff = midnightMs(); // since midnight GMT, matches the rest of the display
  const counts = new Int32Array(CITIES.length);

  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.ts < cutoff) break;
    // Resolve origin lat/lng from srcCountry via a quick nearest-city lookup.
    // We use the same approach as NodeLayer.nearestCity but inline here.
    if (e.srcCountry) {
      const idx = _nearestCityForCountry(e.srcCountry);
      if (idx >= 0) counts[idx]++;
    }
    if (e.dstCountry) {
      const idx = _nearestCityForCountry(e.dstCountry);
      if (idx >= 0) counts[idx]++;
    }
  }

  let maxCount = 0;
  for (let i = 0; i < counts.length; i++) {
    if (counts[i] > maxCount) maxCount = counts[i];
  }

  const result = new Map<number, number>();
  if (maxCount === 0) return result;
  const logMax = Math.log(maxCount + 1);
  for (let i = 0; i < counts.length; i++) {
    result.set(i, Math.log(counts[i] + 1) / logMax);
  }
  return result;
}

/** Map ISO-2 country code to the primary city index for that country. */
const _countryCityIdx = new Map<string, number>(
  CITIES.map((c, i) => [c.country, i]),
);

function _nearestCityForCountry(countryCode: string): number {
  // Direct country match first — fast path for known corridors.
  const direct = _countryCityIdx.get(countryCode.toUpperCase());
  if (direct !== undefined) return direct;
  // Currency code fallback: 3-letter codes like "NGN" → "NG"
  if (countryCode.length === 3) {
    const iso2 = countryCode.slice(0, 2).toUpperCase();
    const fallback = _countryCityIdx.get(iso2);
    if (fallback !== undefined) return fallback;
  }
  return -1;
}

function computeBaselineTotals(
  events: RawEvent[],
  windowStart: number,
): TodayTotals {
  const sinceMs = midnightMs();
  const byProduct = emptyProductTotals();
  const total = { count: 0, volumeUsd: 0 };
  for (const e of events) {
    if (e.ts < sinceMs) continue;
    if (e.ts >= windowStart) continue; // will be counted via live emission
    const p = resolveProduct(e.product);
    byProduct[p].count += 1;
    byProduct[p].volumeUsd += e.usd;
    total.count += 1;
    total.volumeUsd += e.usd;
  }
  return { total, byProduct, sinceMs };
}

/**
 * Replay stream — TAIL MODE.
 *
 * Rather than playing the entire 24h window forward (which meant new data
 * appeared 24h after it arrived), we play the most recent TAIL_WINDOW_MS
 * of events and then wait. Every REFRESH_MS we re-fetch replay.json and
 * append new events. Fresh data appears within minutes of the underlying
 * dbt run completing.
 *
 * Display-visible latency breakdown:
 *   (dbt lag: 0-15 min) + (fetch cron: 0-10 min) + (tail catchup: ~0 min)
 *   = typically 10-25 min end-to-end with a 15-min dbt schedule.
 *
 * If the refresh fails or the cron stops producing new data, the stream
 * silently loops the last tail window so the display never goes dark.
 */

type RawEvent = {
  id: string;
  ts: number;
  product: string | null;
  srcCountry: string | null;
  dstCountry: string | null;
  srcCcy: string | null;
  dstCcy: string | null;
  usd: number;
  srcAmt: number | null;
  dstAmt: number | null;
  txType: string | null;
  status: string | null;
  merchant: string | null;
};

export type DisplayStats = {
  uniqueUsersToday: number;
  avgDeliverySec: number;
  totalRegistered: number;
  newRegistrationsToday: number;
  firstSendersToday: number;
  /** registrationsLastHour / 3_600_000 — users per ms, for smooth interpolation. */
  registrationsPerMs: number;
  /** Today's volume from BQ, anchored to MAX(transaction_date). Used for the
   *  "% vs yesterday" line so both sides are apples-to-apples to the second. */
  todayVolumeUsd: number;
  yesterdayVolumeUsd: number;
  /** Current month-to-date volume (USD). */
  mtdCurrentUsd: number;
  /** Previous month volume up to the same elapsed time of month (USD). */
  mtdPrevUsd: number;
  /** Three-letter abbreviation of the current month (e.g. "May"). */
  currentMonthName: string;
  /** Three-letter abbreviation of the previous month (e.g. "Apr"). */
  prevMonthName: string;
};

type ReplayFileStats = {
  uniqueUsersToday: number;
  avgDeliverySec: number;
  totalRegistered: number;
  newRegistrationsToday: number;
  firstSendersToday: number;
  registrationsLastHour: number;
  todayVolumeUsd?: number;
  yesterdayVolumeUsd?: number;
  mtdCurrentUsd?: number;
  mtdPrevUsd?: number;
  currentMonthName?: string;
  prevMonthName?: string;
};

type ReplayFile = {
  fetchedAt: string;
  windowStart: number | null;
  windowEnd: number | null;
  count: number;
  events: RawEvent[];
  /**
   * How old the data actually is, per source mart. Absent on payloads served
   * by a server older than 2026-09-08.
   */
  freshness?: DataFreshness;
  stats?: ReplayFileStats;
};

/**
 * Newest event per source mart, and the stalest of them. The wall unions
 * three tables on different build cadences, so it is only as fresh as its
 * stalest source — `fetchedAt` measures when the server last talked to
 * BigQuery, which stays green while one source is frozen.
 */
export type DataFreshness = {
  bySource: Record<string, number | null>;
  stalestSource: string | null;
  /** Null means a source reported nothing in the 24h window: maximally stale. */
  stalestEventTs: number | null;
};

export type ReplayOptions = {
  url?: string;
  /** Playback speed. 1 = realtime, 2 = 2x, etc. */
  speed?: number;
  onEvent: (ev: TransactionEvent) => void;
  onReady?: (meta: { count: number; spanMinutes: number }) => void;
  onRefresh?: (meta: { newEvents: number; totalEvents: number }) => void;
  /**
   * Fires with baseline today's totals — events that already occurred today
   * but before the tail window begins. The App adds live-emitted events on
   * top so the display ticks up in real time.
   */
  onTodayTotals?: (totals: TodayTotals) => void;
  /** Fires with top corridors from the real replay feed (not session history). */
  onCorridors?: (corridors: CorridorStat[]) => void;
  /** Fires with top merchants from card spend events. */
  onMerchants?: (merchants: MerchantStat[]) => void;
  /**
   * Fires with a Map<cityIndex, activity 0..1> derived from the last 60 min
   * of real transaction volume. Hot cities (Lagos, Nairobi) approach 1.0;
   * cold cities are near 0.
   */
  onCityActivity?: (activity: Map<number, number>) => void;
  /** Fires with aggregate display stats from BQ (unique users, delivery time, etc). */
  onStats?: (stats: DisplayStats) => void;
  /**
   * Fires with per-source data freshness so the UI can surface genuinely
   * stale data rather than merely a stale fetch.
   */
  onFreshness?: (freshness: DataFreshness) => void;
};


const freshnessOf = (file: ReplayFile): DataFreshness =>
  file.freshness ?? {
    bySource: {},
    stalestSource: null,
    stalestEventTs: new Date(file.fetchedAt).getTime(),
  };

function parseStats(raw: ReplayFileStats | undefined): DisplayStats {
  if (!raw) {
    return {
      uniqueUsersToday: 0,
      avgDeliverySec: 0,
      totalRegistered: 0,
      newRegistrationsToday: 0,
      firstSendersToday: 0,
      registrationsPerMs: 0,
      todayVolumeUsd: 0,
      yesterdayVolumeUsd: 0,
      mtdCurrentUsd: 0,
      mtdPrevUsd: 0,
      currentMonthName: "",
      prevMonthName: "",
    };
  }
  return {
    uniqueUsersToday: raw.uniqueUsersToday,
    avgDeliverySec: raw.avgDeliverySec,
    totalRegistered: raw.totalRegistered,
    newRegistrationsToday: raw.newRegistrationsToday,
    firstSendersToday: raw.firstSendersToday,
    registrationsPerMs: raw.registrationsLastHour / 3_600_000,
    todayVolumeUsd: raw.todayVolumeUsd ?? 0,
    yesterdayVolumeUsd: raw.yesterdayVolumeUsd ?? 0,
    mtdCurrentUsd: raw.mtdCurrentUsd ?? 0,
    mtdPrevUsd: raw.mtdPrevUsd ?? 0,
    currentMonthName: raw.currentMonthName ?? "",
    prevMonthName: raw.prevMonthName ?? "",
  };
}

/**
 * Display lag — how far behind wall-clock we show events. The virtual clock
 * advances at 1× wall-clock rate; events emit when their ts crosses it.
 * At wall=13:05, virtual = 12:05. Refresh at any time is instantly accurate
 * (no session-relative playback catch-up).
 *
 * Adaptive: if dbt is stale beyond 1h, we automatically lag further so we
 * always have queue depth. Floor is 60 min to match the "1h behind" mental
 * model the user expects.
 */
const MIN_LAG_MS = 60 * 60 * 1000; // 1h floor
const QUEUE_DEPTH_MS = 10 * 60 * 1000; // always aim for 10 min of queued events at load

const REFRESH_MS = 10 * 60 * 1000; // 10 min — how often we check for fresh data

function enrich(raw: RawEvent): TransactionEvent | null {
  const product = resolveProduct(raw.product);

  // Card creation events have no geography in the source; seed a random
  // African hub so the "+" burst appears where Eversend's users actually live.
  if (product === "card_creation") {
    const hub = seededHubCity(raw.id);
    return {
      id: raw.id,
      ts: raw.ts,
      origin: { lat: hub.lat, lng: hub.lng, city: hub.city, country: hub.country },
      dest: { lat: hub.lat, lng: hub.lng, city: hub.city, country: hub.country },
      amountUsd: 0,
      amountSrc: 0,
      currencySrc: "USD",
      currencyDst: "USD",
      type: "card",
      status: "completed",
      segment: "consumer",
      product,
    };
  }

  let srcCcy = raw.srcCcy;
  let dstCcy = raw.dstCcy;
  if (
    raw.txType &&
    raw.txType.includes("_") &&
    /^[A-Z]{3,4}_[A-Z]{3,4}$/.test(raw.txType)
  ) {
    const [a, b] = raw.txType.split("_");
    srcCcy = srcCcy ?? a;
    dstCcy = dstCcy ?? b;
  }

  const originCode = raw.srcCountry ?? srcCcy;
  const destCode = raw.dstCountry ?? dstCcy;

  // For card spend, override the destination with the merchant's actual HQ
  // so arcs land at Menlo Park (Meta), Mountain View (Google), etc. instead
  // of all hitting NYC.
  let dest: { lat: number; lng: number; city: string; country: string } | null = null;
  let mLabel: string | undefined;
  if (product === "card" && raw.merchant) {
    const hq = resolveMerchantHQ(raw.merchant);
    if (hq) {
      dest = { lat: hq.lat, lng: hq.lng, city: hq.city, country: hq.country };
    }
    mLabel = merchantLabel(raw.merchant) ?? undefined;
  }

  const corridor = resolveCorridor(originCode, destCode, raw.id);
  if (!corridor) return null;

  return {
    id: raw.id,
    ts: raw.ts,
    origin: {
      lat: corridor.origin.lat,
      lng: corridor.origin.lng,
      city: corridor.origin.city,
      country: corridor.origin.country,
    },
    dest: dest ?? {
      lat: corridor.dest.lat,
      lng: corridor.dest.lng,
      city: corridor.dest.city,
      country: corridor.dest.country,
    },
    amountUsd: raw.usd,
    amountSrc: raw.usd,
    currencySrc: srcCcy ?? "USD",
    currencyDst: dstCcy ?? "USD",
    type: inferType(raw.txType),
    status: inferStatus(raw.status),
    segment: "consumer",
    product,
    merchant: mLabel,
    amountSrcLocal: raw.srcAmt ?? undefined,
    amountDstLocal: raw.dstAmt ?? undefined,
  };
}

function resolveProduct(raw: string | null): Product {
  if (!raw) return "other";
  const p = raw.toLowerCase();
  if (p === "send_money") return "send_money";
  if (p === "add_money") return "add_money";
  if (p === "exchange_money") return "exchange_money";
  if (p === "card_creation") return "card_creation";
  if (p === "card" || p.includes("card")) return "card";
  return "other";
}

function inferType(txType: string | null): TxType {
  if (!txType) return "remit";
  const t = txType.toLowerCase();
  if (t.includes("card")) return "card";
  if (t.includes("wallet")) return "wallet";
  if (t.includes("transfer") || t.includes("mobile") || t.includes("bank"))
    return "payout";
  if (/^[a-z]{3,4}_[a-z]{3,4}$/.test(t)) return "fx";
  return "remit";
}

function inferStatus(status: string | null): TxStatus {
  if (!status) return "completed";
  const s = status.toLowerCase();
  if (s.includes("revers") || s.includes("fail") || s.includes("declin"))
    return "reversed";
  if (s.includes("settl")) return "settled";
  return "completed";
}

async function fetchReplay(url: string): Promise<ReplayFile> {
  const res = await fetch(`${url}?t=${Date.now()}`, { cache: "no-cache" });
  if (!res.ok) throw new Error(`replay fetch failed: ${res.status}`);
  return (await res.json()) as ReplayFile;
}

export type ReplayHandle = {
  stop: () => void;
  /** Force an immediate refresh of replay.json + stats. Used on tab focus. */
  refreshNow: () => Promise<void>;
  /** Reset baseline to today's empty totals — used at day rollover so the
   *  next BQ refresh populates a fresh day. */
  resetForNewDay: () => void;
};

export async function startReplayStream(
  opts: ReplayOptions,
): Promise<ReplayHandle> {
  const { url = "/replay.json", speed = 1, onEvent, onReady, onRefresh } = opts;

  const initial = await fetchReplay(url);
  const events: RawEvent[] = initial.events;
  // Report freshness BEFORE the empty-feed guard. An empty feed is the worst
  // case the badge exists for — every source silent for 24h — so returning
  // early here would leave freshness null and suppress the warning exactly
  // when it matters most.
  opts.onFreshness?.(freshnessOf(initial));
  if (!events.length) {
    console.warn("[replay] empty file — no events to play");
    return {
      stop: () => {},
      refreshNow: async () => {},
      resetForNewDay: () => {},
    };
  }
  const maxTs = events[events.length - 1].ts;
  // Adaptive lag: if dbt is stale, virtual_now still has queue depth.
  // Floor at 60 min so the "1h behind live" mental model holds.
  const lagMs = Math.max(
    MIN_LAG_MS,
    Date.now() - maxTs + QUEUE_DEPTH_MS,
  );
  const startVirtualNow = Date.now() - lagMs;
  const startWall = performance.now();
  const virtualNowAt = (wall: number) => startVirtualNow + (wall - startWall);

  // Cursor: skip past events whose virtual-time has already passed.
  // They'll be accounted for in baseline and not re-emitted live.
  let cursor = events.findIndex((e) => e.ts >= startVirtualNow);
  if (cursor < 0) cursor = events.length;

  // Baseline = ALL today's events up to virtual-now at load.
  // This is why refresh is instantly accurate: we don't wait for 55 min
  // of tail replay to reach the right number.
  opts.onTodayTotals?.(computeBaselineTotals(events, startVirtualNow));
  opts.onCorridors?.(computeTopCorridors(events, 5));
  opts.onMerchants?.(computeTopMerchants(events, 5));
  opts.onCityActivity?.(computeCityActivity(events));
  opts.onStats?.(parseStats(initial.stats));

  onReady?.({
    count: events.length - cursor,
    spanMinutes: lagMs / 60_000,
  });

  let stopped = false;
  let consecutiveRefreshFailures = 0;

  // Background refresh — pulls new events from the regenerated replay.json.
  // Does NOT recompute baseline (that would double-count events already
  // emitted as "live" during the session). Baseline is frozen at load.
  //
  // Resilience: after 3 consecutive failed refreshes (~30 min of silence),
  // reload the page to recover from transient Chrome/network state issues.
  const doRefresh = async () => {
    try {
      const fresh = await fetchReplay(url);
      consecutiveRefreshFailures = 0;
      // Before the empty guard, for the same reason as at load: a feed that
      // goes empty must still be able to say so.
      opts.onFreshness?.(freshnessOf(fresh));
      if (!fresh.events.length) return;
      const currentMax = events[events.length - 1].ts;
      const newEvents = fresh.events.filter((e) => e.ts > currentMax);
      if (newEvents.length) {
        events.push(...newEvents);
        onRefresh?.({
          newEvents: newEvents.length,
          totalEvents: events.length,
        });
      }
      opts.onCorridors?.(computeTopCorridors(events, 5));
      opts.onMerchants?.(computeTopMerchants(events, 5));
      opts.onCityActivity?.(computeCityActivity(events));
      opts.onStats?.(parseStats(fresh.stats));
    } catch (err) {
      consecutiveRefreshFailures += 1;
      console.warn(
        `[replay] refresh failed (${consecutiveRefreshFailures}/3):`,
        err,
      );
      if (consecutiveRefreshFailures >= 3) {
        console.warn("[replay] 3 consecutive refresh failures, reloading page");
        window.location.reload();
      }
    }
  };
  const refreshInterval = setInterval(doRefresh, REFRESH_MS);

  // Speed is supported but only off 1× for testing. At 1× the virtual clock
  // matches wall-clock pacing exactly — the "1 hour behind live" invariant.
  const tick = () => {
    if (stopped) return;
    const wall = performance.now();
    const vn = virtualNowAt(wall) + (wall - startWall) * (speed - 1);

    while (cursor < events.length && events[cursor].ts <= vn) {
      const rawTs = events[cursor].ts;
      const ev = enrich(events[cursor]);
      cursor++;
      if (ev) onEvent({ ...ev, originalTs: rawTs, ts: Date.now() });
    }

    // No tail loop, no idle guard. If data runs dry, display goes quiet
    // honestly — more truthful than replaying phantom activity.
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  return {
    stop: () => {
      stopped = true;
      clearInterval(refreshInterval);
    },
    refreshNow: async () => {
      await doRefresh();
      // Recompute baseline against the latest events so the headline
      // catches up even if the tab was backgrounded for a long time.
      opts.onTodayTotals?.(computeBaselineTotals(events, Date.now() - lagMs));
    },
    resetForNewDay: () => {
      // Drain anything before now so the new day starts clean.
      cursor = events.length;
      // Empty totals for the new day — the next BQ refresh will fill it in.
      opts.onTodayTotals?.(emptyTodayTotals());
    },
  };
}
